<template>
  <div class="peripheral-panel" :class="{ embedded }">
    <!-- 挂载仓控制 -->
    <section v-if="hasCableTransport" class="device-section">
      <div class="device-title gs-section-heading"><span>{{ $t("挂载仓") }}</span></div>
      <div class="device-grid hook-grid">
        <UiButton variant="device" :disabled="disabled || !plane || loadcellAlignPending" @click="startLoadcellAlign"><i :class="loadcellAlignPending ? 'el-icon-loading' : 'iconfont icon-zhongliang'"></i><span>{{ $t("校准") }}</span></UiButton>
        <UiButton variant="device" tone="danger" :disabled="disabled || !plane" @click="handleHookDown"><i class="iconfont icon-xiangxia"></i><span>{{ $t("放钩") }}</span></UiButton>
        <UiButton variant="device" tone="primary" :disabled="disabled || !plane" @click="handleHookRecovery"><i class="iconfont icon-xiangshang"></i><span>{{ $t("收钩") }}</span></UiButton>
        <UiButton variant="device" tone="warning" :disabled="disabled || !plane" @click="sendCommand('{hookConteroller:2}', '暂停放钩')"><i class="iconfont icon-xuanting-zanting"></i><span>{{ $t("暂停") }}</span></UiButton>
        <UiButton variant="device" tone="primary" :disabled="disabled || !plane" @click="handleHookUnload"><i class="iconfont icon-xiangxia"></i><span>{{ $t("重放") }}</span></UiButton>
      </div>
    </section>

    <!-- 抛物舵机控制 -->
    <section v-if="hasParabola" class="device-section">
      <div class="device-title gs-section-heading"><span>{{ $t("抛物模块") }}</span></div>
      <div class="device-grid two-columns">
        <UiButton variant="device" :disabled="disabled || !plane" @click="sendCommand('{&quot;parabolaServo&quot;:1}', '打开抛物舵机')"><i class="el-icon-unlock"></i><span>{{ $t("开舵机") }}</span></UiButton>
        <UiButton variant="device" :disabled="disabled || !plane" @click="sendCommand('{&quot;parabolaServo&quot;:0}', '关闭抛物舵机')"><i class="el-icon-lock"></i><span>{{ $t("关舵机") }}</span></UiButton>
      </div>
    </section>

    <!-- 摄像头控制 -->
    <section v-if="hasCamera" class="device-section">
      <div class="device-title gs-section-heading"><span>{{ $t("摄像头") }}</span></div>
      <div class="device-grid three-columns">
        <UiButton variant="device" :disabled="disabled || !plane" @click="sendCommand('{cameraController:{item:0,val:0}}', '摄像头一键回中')"><i class="iconfont icon-icon-rotation"></i><span>{{ $t("回中") }}</span></UiButton>
        <UiButton variant="device" :disabled="disabled || !plane" @click="sendCommand('{cameraController:{item:3,val:0}}', '摄像头一键俯瞰')"><i class="iconfont icon-down"></i><span>{{ $t("俯瞰") }}</span></UiButton>
        <UiButton variant="device" :active="cameraVisible" :disabled="disabled || !plane" @click="cameraVisible = !cameraVisible"><i class="iconfont icon-chukong"></i><span>{{ $t("手动") }}</span></UiButton>
      </div>
      <div v-if="cameraVisible" class="camera-sliders">
        <label><span>{{ $t("俯仰") }}</span><UiSlider :disabled="disabled || !plane" variant="compact" v-model="pitchValue" :show-tooltip="false" @input="setCamera('pitch')" @change="releaseCamera('pitch')"></UiSlider></label>
        <label><span>{{ $t("旋转") }}</span><UiSlider :disabled="disabled || !plane" variant="compact" v-model="yawValue" :show-tooltip="false" @input="setCamera('yaw')" @change="releaseCamera('yaw')"></UiSlider></label>
        <label><span>{{ $t("焦距") }}</span><UiSlider :disabled="disabled || !plane" variant="compact" v-model="scaleValue" :show-tooltip="false" @input="setCamera('scale')" @change="releaseCamera('scale')"></UiSlider></label>
      </div>
    </section>

    <!-- 喇叭与危险操作 -->
    <section v-if="hasVoice" class="device-section">
      <div class="device-title gs-section-heading"><span>{{ $t("喇叭") }}</span></div>
      <div class="device-grid two-columns">
        <UiButton variant="device" :disabled="disabled || !plane" @click="talkDialogVisible = true"><i class="iconfont icon-icon-test"></i><span>{{ $t("喊话") }}</span></UiButton>
        <UiButton variant="device" :tone="buzzerEnable ? 'primary' : 'neutral'" :active="!buzzerStateUnknown && buzzerEnable" :loading="buzzerSwitchPending" :disabled="disabled || !plane || buzzerSwitchPending" @click="toggleBuzzerEnable"><i v-if="!buzzerSwitchPending" class="iconfont icon-tongzhi"></i><span>{{ $t(buzzerStateText) }}</span></UiButton>
      </div>
    </section>
    <!-- 危险操作固定显示在外设面板最下方 -->
    <section v-if="hasTransportFuse" class="device-section fuse-section">
      <div class="device-title gs-section-heading"><span>{{ $t("安全熔断") }}</span></div>
      <UiButton variant="device" tone="danger" block class="fuse-button" :disabled="disabled || !plane" @click="openFuseSlider"><i class="iconfont icon-baoxiansirongduan"></i><span>{{ $t("挂载绳熔断") }}</span></UiButton>
    </section>

    <div v-if="plane && !hasPeripheralModule" class="empty-device">{{ $t("当前飞机未配置外设模组") }}</div>

    <UiDialog v-model="showFuseSlider" :title="$t('挂载绳熔断确认')" width="420px" custom-class="ground-station-fuse-dialog" @close="closeFuseSlider">
      <p class="fuse-dialog-hint">{{ $t("{0} · 确认周围安全后，滑动以发送熔断指令", [(plane ? plane.name : '未选择飞机')]) }}</p>
      <div class="fuse-dialog-slide">
        <GroundStationSlideConfirm
          :label="$t('按住滑块，向右滑动')"
          ready-text="松开执行熔断"
          icon-class="iconfont icon-baoxiansirongduan"
          :disabled="disabled || !plane"
          @interaction-start="clearFuseSliderTimer"
          @interaction-end="armFuseSliderTimer"
          @confirm="executeFuse"
        />
      </div>
      <span slot="footer"><UiButton action="cancel" @click="closeFuseSlider">{{ $t("取消") }}</UiButton></span>
    </UiDialog>

    <!-- 喊话参数 -->
    <UiDialog v-model="talkDialogVisible" :title="$t('喊话参数')" width="380px" top="25vh" custom-class="ground-station-talk-dialog">
      <UiSelect v-if="talkPresets.length" v-model="talkValue" class="talk-preset" clearable :placeholder="$t('快捷选择喊话预设')">
        <el-option v-for="(item, index) in talkPresets" :key="index" :label="$t(item)" :value="item"></el-option>
      </UiSelect>
      <UiInput v-model="talkValue" type="textarea" :rows="3" :placeholder="$t('请输入喊话内容')" />
      <div class="volume-setting"><span>{{ $t("音量") }}</span><UiSlider v-model="talkVolume" :min="1" :max="9"></UiSlider></div>
      <span slot="footer"><UiButton action="save" @click="playText">{{ $t("发送") }}</UiButton><UiButton action="cancel" @click="talkDialogVisible = false">{{ $t("取消") }}</UiButton></span>
    </UiDialog>
  </div>
</template>

<script>
import { t } from '@/i18n'
import mqtt from '@/utils/mqtt'
import { speakText } from '@/utils/index'
import { hasPeripheralModule as planeHasPeripheralModule, hasPlaneModule } from '@/utils/planeModules'
import GroundStationSlideConfirm from '@/components/ground-station/GroundStationSlideConfirm'

export default {
  name: 'GroundStationPeripheralControlPanel',
  components: { GroundStationSlideConfirm },
  props: {
    plane: { type: Object, default: null },
    disabled: { type: Boolean, default: false },
    embedded: { type: Boolean, default: false }
  },
  data () {
    return { cameraVisible: false, talkDialogVisible: false, showFuseSlider: false, fuseSliderTimer: null, pitchValue: 50, yawValue: 50, scaleValue: 50, cameraTimer: null, talkValue: '', talkVolume: 9, loadcellAlignPending: false, loadcellAlignTimer: null, loadcellAlignFailureTimer: null, buzzerSwitchPending: false, buzzerSwitchTarget: null, buzzerSwitchTimer: null }
  },
  computed: {
    planeState () { return (this.plane && this.plane.planeState) || {} },
    hasCableTransport () { return hasPlaneModule(this.plane, 'cable_transport') },
    hasVoice () { return hasPlaneModule(this.plane, 'voice') },
    hasCamera () { return hasPlaneModule(this.plane, 'camera') },
    hasTransportFuse () { return hasPlaneModule(this.plane, 'transport_fuse') },
    hasParabola () { return hasPlaneModule(this.plane, 'parabola') },
    hasPeripheralModule () { return planeHasPeripheralModule(this.plane) },
    buzzerStateValue () { return this.planeState.deviceSwitchState && this.planeState.deviceSwitchState.buzzerEnable },
    buzzerStateUnknown () { return this.buzzerStateValue === null || typeof this.buzzerStateValue === 'undefined' },
    buzzerEnable () { return [true, 1, '1', 'true'].includes(this.buzzerStateValue) },
    buzzerStateText () { return this.buzzerStateUnknown ? '取状态' : (this.buzzerEnable ? '喇叭开' : '喇叭关') },
    loadcellAlignStatus () { const value = this.planeState.AlignStatus; return value === null || typeof value === 'undefined' ? '' : String(value) },
    talkPresets () {
      if (!this.plane) return []
      const shop = this.$store.state.shopList.find(item => String(item.shop_id) === String(this.plane.shop_id))
      return shop && Array.isArray(shop.voice_presup) ? shop.voice_presup.filter(item => typeof item === 'string' && item.trim()) : []
    }
  },
  watch: {
    plane () { this.cameraVisible = false; this.talkDialogVisible = false; this.clearBuzzerSwitch(); this.closeFuseSlider() },
    buzzerStateValue () {
      if (!this.buzzerSwitchPending || this.buzzerStateUnknown) return
      const enabled = this.buzzerEnable
      if (this.buzzerSwitchTarget !== null && enabled !== this.buzzerSwitchTarget) return
      const target = this.buzzerSwitchTarget
      this.clearBuzzerSwitch()
      if (target !== null) this.$message.success(t(enabled ? '喇叭已开启' : '喇叭已关闭'))
    },
    loadcellAlignStatus (value, oldValue) {
      if (!this.loadcellAlignPending || value === '' || value === '0' || value === oldValue) return
      if (value === '1') {
        this.finishLoadcellAlign('重量传感器已校准', 'success')
        return
      }
      if (this.loadcellAlignFailureTimer) clearTimeout(this.loadcellAlignFailureTimer)
      this.loadcellAlignFailureTimer = setTimeout(() => {
        this.finishLoadcellAlign('重量传感器校准失败', 'error')
      }, 2500)
    }
  },
  beforeDestroy () { if (this.cameraTimer) clearTimeout(this.cameraTimer); if (this.loadcellAlignTimer) clearTimeout(this.loadcellAlignTimer); if (this.loadcellAlignFailureTimer) clearTimeout(this.loadcellAlignFailureTimer); this.clearBuzzerSwitch(); this.clearFuseSliderTimer() },
  methods: {
    publishCommand (command) { if (this.disabled || !this.plane) { this.$message.warning(t(this.disabled ? '飞机离线，无法操作' : '请先选择飞机')); return false }; return mqtt.publishCommand(this.plane, command) },
    writeTerminalLog (content) { if (this.plane && content) this.$store.dispatch('fetchLog', { sourceType: 'terminal', planeId: this.plane.id, planeName: this.plane.name, content }) },
    sendCommand (command, description) { if (!this.publishCommand(command)) return; this.writeTerminalLog(description); speakText(description) },
    handleHookDown () {
      const unlocked = [true, 1, '1'].includes(this.planeState.isUnlock)
      const locked = [false, 0, '0'].includes(this.planeState.isUnlock)
      if (unlocked) return this.$message.warning(t('飞机已解锁，不可操作放钩'))
      if (!locked) return this.$message.warning(t('未确认飞机已加锁，不可操作放钩'))
      this.$confirm(t('确认放钩一次？请确认飞机处于加锁安全状态。'), t('放钩确认'), { confirmButtonText: t('确认放钩'), cancelButtonText: t('取消'), type: 'warning' }).then(() => this.sendCommand('{hookConteroller:1}', '放钩')).catch(() => {})
    },
    handleHookUnload () {
      this.$confirm(t('确认发送重放指令？飞机端仅在飞控已开始本轮自动卸货且当前阶段允许时执行。'), t('重放确认'), { confirmButtonText: t('确认重放'), cancelButtonText: t('取消'), type: 'warning' }).then(() => {
        if (!this.publishCommand('{hookConteroller:5}')) return
        this.writeTerminalLog('发送重放指令')
        speakText('发送重放指令')
        this.$message.info(t('已尝试发送重放指令，是否送达和执行以飞机端为准'))
      }).catch(() => {})
    },
    handleHookRecovery () {
      const execute = () => this.sendCommand('{hookConteroller:0}', '收钩')
      const message = [true, 1, '1'].includes(this.planeState.isAutoGoodsDown) ? '确认收钩？' : '当前不是送餐自动下放过程，确认收钩？'
      this.$confirm(t(message), t('收钩确认'), { confirmButtonText: t('确认收钩'), cancelButtonText: t('取消'), type: 'warning' }).then(execute).catch(() => {})
    },
    startLoadcellAlign () {
      if (this.loadcellAlignTimer) clearTimeout(this.loadcellAlignTimer)
      if (this.plane && this.plane.planeState) this.$set(this.plane.planeState, 'AlignStatus', 0)
      this.loadcellAlignPending = true
      this.sendCommand('{hookConteroller:4}', '重置重量传感器')
      this.loadcellAlignTimer = setTimeout(() => this.finishLoadcellAlign('重量传感器校准超时，请检查飞机端状态', 'warning'), 10000)
    },
    finishLoadcellAlign (message, type) { if (this.loadcellAlignTimer) clearTimeout(this.loadcellAlignTimer); if (this.loadcellAlignFailureTimer) clearTimeout(this.loadcellAlignFailureTimer); this.loadcellAlignTimer = null; this.loadcellAlignFailureTimer = null; this.loadcellAlignPending = false; this.$message[type](message) },
    toggleBuzzerEnable () {
      if (this.buzzerSwitchPending) return
      if (this.buzzerStateUnknown) {
        if (!this.publishCommand('{"refreshRequest":1}')) return
        this.beginBuzzerSwitchWait(null)
        return
      }
      const enabled = !this.buzzerEnable
      if (!this.publishCommand(JSON.stringify({ buzzerEnable: enabled }))) return
      const text = enabled ? '开启喇叭' : '关闭喇叭'
      this.writeTerminalLog(text)
      speakText(text)
      this.beginBuzzerSwitchWait(enabled)
    },
    beginBuzzerSwitchWait (target) {
      this.buzzerSwitchPending = true
      this.buzzerSwitchTarget = target
      this.buzzerSwitchTimer = setTimeout(() => {
        this.clearBuzzerSwitch()
        this.$message.warning(t(target === null ? '未获取到喇叭实时状态' : '喇叭开关响应超时，请检查飞机端状态'))
      }, 10000)
    },
    clearBuzzerSwitch () {
      if (this.buzzerSwitchTimer) clearTimeout(this.buzzerSwitchTimer)
      this.buzzerSwitchTimer = null
      this.buzzerSwitchPending = false
      this.buzzerSwitchTarget = null
    },
    playText () { if (!this.talkValue.trim()) return this.$message.warning(t('请输入喊话内容')); if (!this.publishCommand(JSON.stringify({ playText: { val: this.talkValue, vol: parseInt(this.talkVolume) } }))) return; this.writeTerminalLog(`喇叭播放：${this.talkValue}`); this.talkDialogVisible = false },
    openFuseSlider () { if (this.plane) { this.showFuseSlider = true; this.armFuseSliderTimer() } },
    closeFuseSlider () { this.clearFuseSliderTimer(); this.showFuseSlider = false },
    clearFuseSliderTimer () { if (this.fuseSliderTimer) clearTimeout(this.fuseSliderTimer); this.fuseSliderTimer = null },
    armFuseSliderTimer () { this.clearFuseSliderTimer(); if (this.showFuseSlider) this.fuseSliderTimer = setTimeout(() => this.closeFuseSlider(), 10000) },
    executeFuse () { this.sendCommand('{"fuseCutoff":1}', '执行挂载绳熔断'); this.closeFuseSlider() },
    setCamera (item) { if (this.cameraTimer) clearTimeout(this.cameraTimer); this.cameraTimer = setTimeout(() => { if (item === 'pitch') this.publishCommand(`{cameraController:{item:2,yaw:0,pitch:${(this.pitchValue - 50) * 2}}}`); if (item === 'yaw') this.publishCommand(`{cameraController:{item:2,yaw:${(this.yawValue - 50) * 2},pitch:0}}`); if (item === 'scale') this.publishCommand(`{cameraController:{item:1,val:${this.scaleValue > 50 ? 1 : -1}}}`) }, 150) },
    releaseCamera (item) { if (item === 'pitch') this.pitchValue = 50; if (item === 'yaw') this.yawValue = 50; if (item === 'scale') this.scaleValue = 50; this.publishCommand(item === 'scale' ? '{cameraController:{item:1,val:0}}' : '{cameraController:{item:2,val:0,yaw:0,pitch:0}}') }
  }
}
</script>

<style lang="scss" scoped>
.peripheral-panel {
  display: grid;
  gap: var(--ui-space-sm);
  padding: 0 var(--ui-space-sm) var(--ui-space-md);
}

.peripheral-panel.embedded { padding: 0; }

.device-section {
  min-width: 0;
  padding: var(--ui-space-sm);
  border: 1px solid var(--ui-border-color);
  border-radius: 6px;
  background: var(--ui-bg-control);
}

.device-title { min-height: 16px; display: flex; align-items: center; margin-bottom: var(--ui-space-sm); }
.device-grid { display: grid; gap: var(--ui-space-sm); }
.three-columns { grid-template-columns: repeat(3, 1fr); }
.hook-grid { grid-template-columns: repeat(6, minmax(0, 1fr)); }
.hook-grid > :nth-child(-n+2) { grid-column: span 3; }
.hook-grid > :nth-child(n+3) { grid-column: span 2; }
.two-columns { grid-template-columns: repeat(2, 1fr); }
.fuse-section { padding-bottom: var(--ui-space-sm); }
.fuse-button { border-color: rgba(var(--ui-color-danger-rgb), .35); }
.fuse-dialog-hint { margin: 0 0 var(--ui-space-lg); color: var(--ui-text-secondary); font-size: var(--ui-font-size-body); line-height: 1.5; }
.fuse-dialog-slide ::v-deep .slide-confirm { height: 64px; padding-left: 64px; box-sizing: border-box; border: 1px solid rgba(var(--ui-color-danger-emphasis-rgb), .35); border-radius: 8px; background: var(--ui-bg-control); color: var(--ui-text-secondary); font-size: var(--ui-font-size-body); }
.fuse-dialog-slide ::v-deep .slide-confirm-handle { width: 56px; height: 56px; left: 4px; top: 3px; border-radius: 6px; box-shadow: 0 2px 8px rgba(var(--ui-color-danger-emphasis-rgb), .2); }
.camera-sliders { padding: var(--ui-space-sm) var(--ui-space-xs) 0; }
.camera-sliders label { display: grid; grid-template-columns: 34px 1fr; align-items: center; color: var(--ui-text-muted); }
.empty-device { padding: var(--ui-space-lg) var(--ui-space-sm); color: var(--ui-text-disabled); text-align: center; }
.talk-preset { width: 100%; margin-bottom: var(--ui-space-sm); }
.volume-setting { display: grid; grid-template-columns: 36px 1fr; align-items: center; margin-top: var(--ui-space-sm); }
.camera-sliders label { font-size: var(--gs-font-micro, 10px); font-weight: var(--gs-weight-regular, 400); }
.empty-device { font-size: var(--gs-font-caption, 11px); }
</style>

<style lang="scss">
.ground-station-fuse-dialog.el-dialog { position: fixed; top: 50%; left: 50%; width: min(420px, calc(100vw - 32px)) !important; margin: 0 !important; transform: translate(-50%, -50%); }
.ui-dialog-wrapper.dialog-fade-enter-active > .ui-dialog.ground-station-fuse-dialog:not(.is-fullscreen) {
  animation: ground-station-fuse-dialog-in var(--ui-duration-normal) var(--ui-ease-standard);
}
.ui-dialog-wrapper.dialog-fade-leave-active > .ui-dialog.ground-station-fuse-dialog:not(.is-fullscreen) {
  animation: ground-station-fuse-dialog-in var(--ui-duration-fast) ease reverse;
}
@keyframes ground-station-fuse-dialog-in {
  from { transform: translate(-50%, -50%) scale(.98); }
  to { transform: translate(-50%, -50%) scale(1); }
}
</style>
